/**
 * Utility functions for the frontend
 */
import { FirebaseError } from 'firebase/app';
import { sendSignInLinkToEmail, User } from 'firebase/auth';
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  getDocs,
  PartialWithFieldValue,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { navigate } from 'gatsby';
import ky from 'ky';
import { logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';
import { apiUrls, isLiveEnv, mobileDeepLinkConfig, pages, storageKeys } from '../../config';
import { RecordingFiltersObj } from '../components/RecordingFilters';
import { analytics, auth, db, functions } from '../firebase';
import logger from '../logger';
import {
  MemberPlaylist,
  MemberProfileData,
  Membership,
  Playlist,
  Podcast,
  PromoCodeProps,
  RecordingPreviewData,
  ScholarshipId,
  SubscriptionInfoType,
} from '../types';
import { notEmpty, recordingToPlaylistItem } from './common';
import { MembershipPlanType } from '../components/MembershipPlans/types';
import fetchWithCache from './fetchWithCache';

// SSR-safe. The maxTouchPoints check catches iPad Pro on iOS 13+ which reports
// as Macintosh in userAgent but still uses iOS's large MediaStream buffer.
export const isIOS =
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)));

export const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

export function checkIfDesktopApp(): boolean {
  const userAgent = typeof window === 'undefined' ? '' : window.navigator.userAgent;
  return userAgent.includes('Electron') && userAgent.includes('earth.fm');
}

/**
 * Detects if the user is on a mobile device (phone or tablet)
 * Returns true for iOS and Android devices
 * @returns boolean - true if on iOS or Android, false otherwise
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || '';

  // iOS detection (including iPad desktop mode workaround)
  const isIOSPlatform =
    /iphone|ipad|ipod/.test(userAgent) || (platform === 'macintel' && navigator.maxTouchPoints > 1);

  return isIOSPlatform || isAndroid;
}

/**
 * Redirects user to app store subscription settings based on provider
 * @param provider - The subscription provider ('apple.com' or 'google.com')
 * @returns boolean - true if redirect was attempted, false if provider is unsupported
 */
export function redirectToAppSubscriptionSettings(
  provider: 'apple.com' | 'google.com' | 'stripe.com' | 'unknown',
): boolean {
  if (typeof window === 'undefined') return false;

  try {
    if (provider === 'apple.com') {
      // iOS App Store subscriptions page
      window.location.href = 'https://apps.apple.com/account/subscriptions';
      return true;
    }

    if (provider === 'google.com') {
      // Google Play Store subscriptions page
      window.location.href = 'https://play.google.com/store/account/subscriptions';
      return true;
    }

    return false;
  } catch (error) {
    logger.error('Failed to redirect to app subscription settings', error);
    return false;
  }
}

export async function emailSignInLink(email: string) {
  const isDesktopApp = checkIfDesktopApp();
  const { iosBundleId, androidPackageName, authLinkDomain } = mobileDeepLinkConfig;

  const redirectAfterSignIn = window.localStorage.getItem(storageKeys.redirectAfterSignIn);
  const isWebOnlyFlow =
    redirectAfterSignIn !== null ||
    window.localStorage.getItem(storageKeys.giftCard) !== null ||
    window.localStorage.getItem(storageKeys.scholarship) !== null;

  const mobileDeepLink = !isDesktopApp
    ? {
        ...(!isWebOnlyFlow && iosBundleId && { iOS: { bundleId: iosBundleId } }),
        ...(!isWebOnlyFlow &&
          androidPackageName && { android: { packageName: androidPackageName } }),
        ...(authLinkDomain && { linkDomain: authLinkDomain }),
      }
    : {};

  let url = window.location.origin;
  if (isDesktopApp) {
    url = `${window.location.origin}${pages.desktopAppSignIn}`;
  } else if (redirectAfterSignIn) {
    url = `${window.location.origin}${redirectAfterSignIn}`;
  }

  const actionCodeSettings = {
    url,
    handleCodeInApp: true,
    ...mobileDeepLink,
  };

  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
}

/**
 * Maps a role string (from either Stripe or RevenueCat) to a Membership enum value.
 * This function handles both current and legacy membership plans.
 */
export function mapRoleToMembership(role: string | undefined): Membership {
  if (!role) {
    return Membership.None;
  }

  switch (role) {
    case 'individual_monthly':
    case 'individual_yearly':
    case 'individual_lifetime':
      return Membership.Individual;

    case 'family_monthly':
    case 'family_yearly':
    case 'family_monthly_1':
    case 'family_yearly_1':
    case 'family_lifetime':
      return Membership.Family;

    case 'business_monthly':
    case 'business_yearly':
      return Membership.Business;

    // Below are the legacy membership plans,
    // keeping it for the existing members
    case 'friend':
    case 'supporter':
      return Membership.Friend;

    case 'partner':
      return Membership.Partner;

    default:
      return Membership.None;
  }
}

/**
 * Gets the membership status for a user, checking both Stripe roles and RevenueCat entitlements.
 * Priority order:
 * 1. Stripe role (for web subscribers)
 * 2. RevenueCat entitlements (for mobile subscribers using the web)
 */
export async function getMembership(user: User | null): Promise<Membership> {
  if (!user) {
    return Membership.None;
  }

  const decodedToken = await user.getIdTokenResult(true);
  const { stripeRole, revenueCatEntitlements } = decodedToken.claims;

  // Check Stripe role first (web subscribers)
  const stripeMembership = mapRoleToMembership(stripeRole as string | undefined);
  if (stripeMembership !== Membership.None) {
    return stripeMembership;
  }

  // Fallback to RevenueCat entitlements (mobile subscribers accessing web)
  if (revenueCatEntitlements && Array.isArray(revenueCatEntitlements)) {
    const revenueCatRole = revenueCatEntitlements[0];
    return mapRoleToMembership(revenueCatRole);
  }

  return Membership.None;
}

/**
 * Automatically redirect the user after sign-in
 *
 * If user is redirected from scholarship page, redirect to the scholarship page.
 * If user is signed in, and has no membership, redirect to the membership page.
 * If user is signed in, and has a membership, redirect to the home page.
 * If user is not signed in, do nothing.
 */
export async function autoRedirectAfterSignIn(user: User | null) {
  const scholarshipStorageValue = window.localStorage.getItem(storageKeys.scholarship);
  const giftCardStorageValue = window.localStorage.getItem(storageKeys.giftCard);
  const redirectAfterSignIn = window.localStorage.getItem(storageKeys.redirectAfterSignIn);

  if (!user) {
    return;
  }

  let membership: Membership | null = null;

  try {
    membership = await getMembership(user);
  } catch (error) {
    logger.error('Error getting user membership', error);
  }

  if (redirectAfterSignIn) {
    // redirect to the page that the user was trying to access before sign-in
    navigate(redirectAfterSignIn, { replace: true });
    window.localStorage.removeItem(storageKeys.redirectAfterSignIn);
    return;
  }

  if (scholarshipStorageValue && scholarshipStorageValue === 'scholarship_requested') {
    // redirect to the scholarship page if the user is sign in to request scholarship
    navigate(pages.scholarship, { replace: true });
    window.localStorage.removeItem(storageKeys.scholarship);
    return;
  }

  if (giftCardStorageValue) {
    // if the user is redirected from the gift card link and signed in with email
    // redirect to the sign in process with the gift card params
    // to create Stripe checkout session with promotion code
    const { membershipType, promotionCode } = JSON.parse(giftCardStorageValue);
    if (membershipType && promotionCode) {
      navigate(
        `${pages.signIn}?redirectFrom=giftCard&membershipType=${membershipType}&promotionCode=${promotionCode}`,
        { replace: true },
      );
      window.localStorage.removeItem(storageKeys.giftCard);
    }
    return;
  }

  if (membership && membership === Membership.None) {
    navigate(pages.becomeAMember, { replace: true });
  } else {
    navigate(pages.webapp, { replace: true }); // redirect to /web (main app) after sign-in
  }
}

export async function getMemberProfileData(uid: string): Promise<MemberProfileData | null> {
  if (!uid) {
    return null;
  }

  const snap = await getDoc(doc(db, 'members', uid, 'public', 'profile'));
  if (snap.exists()) {
    return {
      ...snap.data(),
      id: uid,
    } as MemberProfileData;
  }
  return null;
}

/**
 * Get basic member data with fallbacks for users without completed profiles.
 * This function tries to get the member profile first, and if that doesn't exist,
 * it falls back to basic Firebase Auth user data (from members collection).
 */
export async function getMemberBasicData(
  uid: string,
): Promise<{ id: string; name: string } | null> {
  if (!uid) {
    return null;
  }

  // First try to get the full profile
  const profile = await getMemberProfileData(uid);
  if (profile?.name) {
    return {
      id: uid,
      name: profile.name,
    };
  }

  // If no profile exists, try to get basic user data from the members collection
  // This should contain Firebase Auth data like displayName and email
  try {
    const memberSnap = await getDoc(doc(db, 'members', uid));
    if (memberSnap.exists()) {
      const memberData = memberSnap.data();

      // Try displayName first, then extract username from email
      let name = memberData.displayName || memberData.name;
      if (!name && memberData.email) {
        // Extract the part before @ from email
        [name] = memberData.email.split('@');
      }

      return {
        id: uid,
        name: name || 'Unknown',
      };
    }
  } catch (error) {
    logger.error('Error getting member basic data', error);
  }

  return {
    id: uid,
    name: 'Unknown',
  };
}

export async function updateMemberProfileData(
  uid: string,
  data: PartialWithFieldValue<MemberProfileData>,
): Promise<void> {
  if (!uid || !data) {
    return;
  }

  await setDoc(doc(db, 'members', uid, 'public', 'profile'), data, { merge: true });
}

export async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function getImageSizeFromDataUrl(
  dataUrl: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function subscribeToNewsletter(email: string, url = apiUrls.newsletterSubscription) {
  const result = await ky
    .post(url, {
      json: { email },
    })
    .json<{ success: boolean }>();

  if (!result.success) {
    throw new Error('Something went wrong!');
  }
}

export function playlistRef(playlistId: string): DocumentReference<DocumentData> {
  return doc(db, 'playlists', playlistId);
}

export function favoritesRef(memberId: string): DocumentReference<DocumentData> {
  return doc(db, 'members', memberId, 'private', 'favorites');
}
export function getRecordingData(id: number, timestamp = 1): Promise<RecordingPreviewData | null> {
  return fetchWithCache<RecordingPreviewData>(`/static-api/recordings/${id}.json?v=${timestamp}`);
}

export async function getAllRecordingsData(v = 1): Promise<RecordingPreviewData[]> {
  const data = await fetchWithCache<RecordingPreviewData[]>(
    `/static-api/recordings/all.json?v=${v}`,
  );
  return data ?? [];
}

export function getPodcastData(id: number, timestamp = 1): Promise<Podcast | null> {
  return fetchWithCache<Podcast>(`/static-api/podcasts/${id}.json?v=${timestamp}`);
}

export async function getAllPodcastsData(v = 1): Promise<Podcast[]> {
  const data = await fetchWithCache<Podcast[]>(`/static-api/podcasts/all.json?v=${v}`);
  return data ?? [];
}

export function getEarthFmPlaylist(id: string, timestamp = 1): Promise<Playlist | null> {
  return fetchWithCache<Playlist>(`/static-api/playlists/${id}.json?v=${timestamp}`);
}

export async function getAllEarthFmPlaylists(v = 1): Promise<Playlist[]> {
  const data = await fetchWithCache<Playlist[]>(`/static-api/playlists/all.json?v=${v}`);
  return data ?? [];
}

export async function memberPlaylistToPlaylist(data: MemberPlaylist): Promise<Playlist | null> {
  const owner = await getMemberBasicData(data.owner);

  const recordings = await Promise.all((data.items || []).map(getRecordingData));
  const items = recordings.map((recording) => {
    if (!recording) {
      return null;
    }
    return recordingToPlaylistItem(recording);
  });

  return {
    id: data.id,
    title: data.name,
    subtitle: data.description,
    thumbnail: data.thumbnail,
    public: data.public,
    owner: {
      id: data.owner,
      name: owner?.name || 'Unknown',
    },
    uri: `${pages.memberPlaylistBase}${data.id}`,
    items: items.filter(notEmpty),
  } as Playlist;
}

export async function getMemberPlaylist(id: string): Promise<MemberPlaylist | null> {
  let snap: DocumentSnapshot;
  try {
    snap = await getDoc(playlistRef(id));
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'permission-denied') {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Permission denied when getting playlist', error);
      }
      // do nothing on production
      // this most commonly happens when a previously shared playlist by someone is no longer public
      return null;
    }
    throw error;
  }
  if (snap.exists()) {
    const data = snap.data() as Omit<MemberPlaylist, 'id'>;
    return { ...data, id: snap.id };
  }
  return null;
}

export async function getEnrichedMemberPlaylist(id: string): Promise<Playlist | null> {
  const memberPlaylist = await getMemberPlaylist(id);
  if (memberPlaylist) {
    const playlist = await memberPlaylistToPlaylist(memberPlaylist);
    return playlist;
  }
  return null;
}

export async function getAllMemberPlaylists(uid: string): Promise<MemberPlaylist[]> {
  const querySnapshot = await getDocs(
    query(collection(db, 'playlists'), where('owner', '==', uid)),
  );
  const memberPlaylists: MemberPlaylist[] = [];
  querySnapshot.forEach((snap) => {
    memberPlaylists.push({ id: snap.id, ...(snap.data() as Omit<MemberPlaylist, 'id'>) });
  });
  return memberPlaylists;
}

export async function addItemToPlaylist(playlistId: string, recordingId: number): Promise<void> {
  await updateDoc(playlistRef(playlistId), {
    items: arrayUnion(recordingId),
  });
}

export async function removeItemFromPlaylist(
  playlistId: string,
  recordingId: number,
): Promise<void> {
  await updateDoc(playlistRef(playlistId), {
    items: arrayRemove(recordingId),
  });
}

export async function addItemToLibrary(
  collectionName: 'recordings' | 'playlists' | 'podcasts',
  itemId: number | string,
  memberId: string,
): Promise<void> {
  const docRef = favoritesRef(memberId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    await setDoc(docRef, {
      [collectionName]: [itemId],
    });
  } else {
    await updateDoc(docRef, {
      [collectionName]: arrayUnion(itemId),
    });
  }
}

export async function removeItemFromLibrary(
  collectionName: 'recordings' | 'playlists' | 'podcasts',
  itemId: number | string,
  memberId: string,
): Promise<void> {
  await updateDoc(favoritesRef(memberId), {
    [collectionName]: arrayRemove(itemId),
  });
}

export async function safeAudioPlay(playResponse: Promise<void> | void): Promise<void> {
  if (playResponse instanceof Promise) {
    playResponse.catch(() => {
      // empty catch to avoid unhandled promise rejection error
    });
  }
}

export function filterRecordings<
  Recording extends {
    mood: number | null;
    region: string | null;
    predominantSound: number | null;
    habitat: number | null;
    recordist: number[] | null;
    duration: number;
    isMeditation: boolean | null;
  },
>(recordings: Recording[], filters: RecordingFiltersObj): Recording[] {
  return recordings
    .filter((recording) => {
      if (filters.mood.length > 0) {
        return recording.mood && filters.mood.includes(recording.mood);
      }
      return true;
    })
    .filter((recording) => {
      if (filters.predominantSound.length > 0) {
        return (
          recording.predominantSound &&
          filters.predominantSound.includes(recording.predominantSound)
        );
      }
      return true;
    })
    .filter((recording) => {
      if (filters.habitat.length > 0) {
        return recording.habitat && filters.habitat.includes(recording.habitat);
      }
      return true;
    })
    .filter((recording) => {
      if (filters.region.length > 0) {
        return recording.region && filters.region.includes(recording.region);
      }
      return true;
    })
    .filter((recording) => {
      if (filters.recordist.length > 0) {
        return (
          recording.recordist && recording.recordist.some((r) => filters.recordist.includes(r))
        );
      }
      return true;
    })
    .filter((recording) => {
      if (filters.duration.length > 0) {
        return filters.duration.some((durationRange) => {
          const [startStr, endStr] = durationRange.split('-');
          const start = Number.parseInt(startStr, 10) * 60;
          const end = Number.parseInt(endStr, 10) * 60;
          return start <= recording.duration && recording.duration <= end;
        });
      }
      return true;
    })
    .filter((recording) => {
      if (filters.meditation.length > 0) {
        return filters.meditation.includes(recording.isMeditation === true);
      }
      return true;
    });
}

export function recordingRawDataToPreviewData(
  recording: Queries.RecordingCardDataFragment,
): RecordingPreviewData | null {
  const { id, title, uri, featuredImage, recordingSettings } = recording;

  if (
    !id ||
    !title ||
    !uri ||
    !recordingSettings?.audioUrl ||
    !recordingSettings?.audio?.mediaDetails ||
    !recordingSettings.recordists
  ) {
    return null;
  }

  return {
    id,
    title,
    uri,
    userFriendlyTitle: recordingSettings.userFriendlyTitle || null,
    featuredImage: featuredImage?.node.localFile?.childImageSharp?.gatsbyImageData || null,
    duration: recordingSettings.audio.mediaDetails.length || 0,
    isLivestream: recordingSettings.isLivestream || false,
    isMeditation: recordingSettings.isMeditation || null,
    isRadio: recordingSettings.isRadio || null,
    audio: recordingSettings.audioUrl,
    recordists: recordingSettings.recordists.filter(notEmpty),
    location: null,
    habitat: null,
    mood: null,
    predominantSound: null,
    isFree: recordingSettings.isFree ?? false,
  };
}

export function formatPrice(price: number | undefined, currency: string = 'usd'): string {
  if (price === undefined) return '';

  // Only show fraction digits if the price has meaningful decimals (not .00)
  const hasDecimals = price % 1 !== 0;

  // Ensure currency code is uppercase for Intl.NumberFormat (ISO 4217 standard)
  const currencyCode = currency.toUpperCase();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(price);
}

export function logCustomEvent(eventName: string, eventParams?: Record<string, unknown>): void {
  // only log events on production
  if (analytics && window.location.hostname === 'earth.fm') {
    logEvent(analytics, eventName, eventParams);
  } else {
    // eslint-disable-next-line no-console
    console.debug('logCustomEvent', eventName, eventParams);
  }
}

export function getMembershipEventParams(plan: MembershipPlanType, transactionId?: string) {
  return {
    transaction_id: transactionId,
    currency: 'USD',
    value: plan.price,
    shipping: 0,
    tax: 0,
    items: [
      {
        item_id: plan.id,
        item_name: plan.name,
        quantity: 1,
        price: plan.price,
        affiliation: 'earth.fm',
        item_brand: 'earth.fm',
        item_category: 'Membership',
      },
    ],
  };
}

function delay(ms: number) {
  return new Promise((res) => {
    setTimeout(res, ms);
  });
}

// Set the user source (web) on a member document in Firestore.
// When used immediately after signing in, the call may fail due to a race condition with Firebase Auth,
// so we retry a few times.
async function retrySetSource(uid: string, source: 'web' = 'web') {
  const maxAttempts = 10;
  const delayMs = 500;

  async function attempt(currentAttempt: number): Promise<void> {
    try {
      await setDoc(doc(db, 'members', uid), { source }, { merge: true });
    } catch (err: any) {
      if (err.code !== 'firestore/permission-denied') {
        logger.error('Error setting source on member document', err);
      }
      if (currentAttempt >= maxAttempts - 1) {
        logger.error('Max attempts reached for setting source on member document', err);
        return;
      }
      await delay(delayMs);
      await attempt(currentAttempt + 1);
    }
  }
  return attempt(0);
}

export function logAuthEvent(isNewUser: boolean, user: User): void {
  if (isNewUser) {
    logCustomEvent('sign_up', {
      method: user.providerId,
    });
    // Set source to 'web' for new users from web signup
    retrySetSource(user.uid, 'web');
  } else {
    logCustomEvent('login');
  }
}

export function logPlayerEvent(
  eventName: string,
  recordingId: number,
  eventSource: string,
  additionalParams: Record<string, unknown> = {},
) {
  getRecordingData(recordingId).then((recordingData) => {
    if (!recordingData) {
      return;
    }

    logCustomEvent(eventName, {
      author: recordingData.recordists.map((recordist) => recordist.title).join(', '),
      title: recordingData.title,
      duration: recordingData.duration,
      mood: recordingData.mood,
      sound: recordingData.predominantSound,
      habitat: recordingData.habitat,
      is_station: recordingData.isRadio,
      is_meditation: recordingData.isMeditation,
      event_source: eventSource,
      ...additionalParams,
    });
  });
}

export function isVisibleByPostSettings(node: {
  postSettings?: { hideFromProduction?: boolean | null } | null;
}): boolean {
  return !(isLiveEnv && node.postSettings?.hideFromProduction);
}

export function getVisiblePlaylists(allPlaylists: Readonly<Queries.PlaylistCardDataFragment[]>) {
  // hide playlists that are marked as hidden in production
  if (isLiveEnv) {
    return allPlaylists.filter((playlist) => !playlist.playlistSettings?.hideFromProduction);
  }

  return allPlaylists;
}

export async function getStripePromotionCode(
  scholarshipId: ScholarshipId,
  email: string,
): Promise<PromoCodeProps | null> {
  if (!scholarshipId || !email || email === '') {
    return null;
  }

  try {
    const createPromotionCode = httpsCallable<
      {
        email: string;
        scholarshipId: ScholarshipId;
      },
      { promoCode: PromoCodeProps }
    >(functions, 'createScholarshipCode');

    const result = await createPromotionCode({
      email,
      scholarshipId,
    });

    const { promoCode } = result.data;
    return promoCode;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting promotion code', error);
    return null;
  }
}

export function updateScholarshipStorageValue(request: string): void {
  const scholarshipStorageValue = window.localStorage.getItem(storageKeys.scholarship);
  const isUserFinishingSignInWithEmail = window.localStorage.getItem('emailForSignIn');

  switch (request) {
    case 'reset':
      if (scholarshipStorageValue && !isUserFinishingSignInWithEmail) {
        window.localStorage.removeItem(storageKeys.scholarship);
      }
      break;
    case 'scholarshipRequested':
      window.localStorage.setItem(storageKeys.scholarship, 'scholarship_requested');
      break;
    default:
      // eslint-disable-next-line no-console
      console.error('Invalid request:', request);
      break;
  }
}

export function shuffleArray<T>(array: T[]) {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
}

export async function getSubscriptionInfo(uid: string): Promise<SubscriptionInfoType | null> {
  const subscriptionInfo = httpsCallable<{ uid: string }, SubscriptionInfoType>(
    functions,
    'getSubscriptionInfo',
  );

  try {
    const { data } = await subscriptionInfo({ uid });
    return data;
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'functions/not-found') {
      return null;
    }
    logger.error('Error getting subscription info', error);
    throw error;
  }
}
